import supabaseService from './supabaseService';
import { AppNotification } from '../models';
import { toast } from 'react-hot-toast';
import { messaging, getToken, onMessage } from './firebaseConfig';

// 🔑 مفتاح VAPID (من Firebase Console)
const VAPID_KEY = 'BJrxDCtjMWFxhqbE9Cug5cfd9nZAQkhvM-662OyNa6Doz0L2PpYTrrPX8NdMThsAQB4FZ6288CqRaNFUArcPQIM';

class NotificationService {
  constructor() {
    this.isInitialized = false;
    this.permission = 'default';
    this.fcmToken = null;
    this.messaging = messaging;
  }

  async initialize(userId) {
    try {
      console.log('🔔 Initializing notification service for user:', userId);

      if ('Notification' in window) {
        this.permission = await Notification.requestPermission();
        console.log('📱 Permission:', this.permission);

        if (this.permission === 'granted') {
          await this.registerServiceWorker();
          if (this.messaging && userId) {
            await this.getFCMToken(userId);
          }
          this.setupForegroundListener();
        } else {
          toast.error('الإشعارات معطلة. يرجى تفعيلها من الإعدادات');
        }
      } else {
        console.warn('⚠️ Browser does not support notifications');
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Init Error:', error);
      this.isInitialized = false;
    }
  }

  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
        console.log('✅ SW Registered:', registration.scope);
        await navigator.serviceWorker.ready;
        return registration;
      } catch (error) {
        console.error('❌ SW Registration failed:', error);
        throw error;
      }
    }
    return null;
  }

  async getFCMToken(userId) {
    if (!this.messaging) return null;

    try {
      const currentToken = await getToken(this.messaging, { vapidKey: VAPID_KEY });

      if (currentToken) {
        console.log('✅ Token obtained');
        this.fcmToken = currentToken;
        // حفظ التوكن في Supabase
        await supabaseService.saveFCMToken(userId, currentToken);
        return currentToken;
      }
      return null;
    } catch (error) {
      console.error('❌ Get Token Error:', error);
      return null;
    }
  }

  setupForegroundListener() {
    if (!this.messaging) return;
    onMessage(this.messaging, (payload) => {
      console.log('📨 Foreground Msg:', payload);
      if (payload.notification) {
        toast(payload.notification.body, { icon: '🔔' });
        if (this.permission === 'granted') {
          new Notification(payload.notification.title, {
            body: payload.notification.body,
            icon: '/favicon.ico'
          });
        }
      }
    });
  }

  /**
   * إرسال إشعار لمستخدم آخر (يتطلب Backend)
   */
  async sendPushNotification(recipientUserId, title, body, data = {}) {
    try {
      // 1. جلب FCM Token من الجدول الصحيح (user_tokens) والعمود الصحيح (fcm_token)
      const { data: tokenData, error } = await supabaseService.client
        .from('user_tokens')
        .select('fcm_token')
        .eq('user_id', recipientUserId)
        .single();

      if (error || !tokenData) {
        console.warn('⚠️ No FCM Token found for user:', recipientUserId);
        if(error) console.error('Error details:', error);
        return false;
      }

      const fcmToken = tokenData.fcm_token;

      // 2. تحديد رابط Edge Function
      const PROJECT_REF = 'reescgwvnnbvwgtadvdv'; 
      const functionUrl = `https://${PROJECT_REF}.supabase.co/functions/v1/send-notification`;

      // 3. إرسال الطلب
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseService.client.supabaseKey}`, 
          'apikey': supabaseService.client.supabaseKey
        },
        body: JSON.stringify({
          token: fcmToken,
          notification: { title, body },
          data: data
        })
      });

      if (response.ok) {
        console.log('✅ Push notification sent via Edge Function');
        return true;
      } else {
        const errorText = await response.text();
        console.error('❌ Edge Function failed:', errorText);
        return false;
      }

    } catch (error) {
      console.error('❌ Error sending push notification:', error);
      return false;
    }
  }

  // --- دوال الإشعار الخاصة بالتطبيق ---

  // Send notification to provider when a new request is received
  async notifyNewRequest(providerId, request) {
    console.log('🚀 بدء عملية إرسال إشعار طلب جديد...');

    // 1. محاولة الحفظ في قاعدة البيانات (محاولة مستقلة)
    try {
      const notification = new AppNotification({
        userId: providerId,
        title: 'طلب جديد',
        message: `لديك طلب جديد من ${request.client?.fullName || 'عميل'} لخدمة ${request.service?.name || 'خدمة'}`,
        isRead: false,
        createdAt: new Date(),
      });
      
      const saved = await supabaseService.createNotification(notification);
      if (saved) {
        console.log('✅ تم حفظ الإشعار بنجاح');
      } else {
        console.warn('⚠️ فشل حفظ الإشعار، لكن سنستمر في الإرسال للهاتف');
      }
    } catch (dbError) {
      console.error('فشل الحفظ (Catch Block):', dbError);
    }

    // 2. إرسال Push Notification للهاتف (محاولة مستقلة)
    try {
      const pushSuccess = await this.sendPushNotification(
        providerId, 
        'طلب جديد 🔔', 
        `لديك طلب جديد لخدمة ${request.service?.name}`, 
        { type: 'new_request', requestId: request.id }
      );

      if (pushSuccess) {
        console.log('📲 تم إرسال الإشعار للهاتف بنجاح');
      } else {
        console.error('❌ فشل إرسال الإشعار للهاتف');
      }
    } catch (pushError) {
      console.error('❌ خطأ في دالة الإرسال:', pushError);
    }

    // 3. رسالة تأكيد للمستخدم المرسل
    this.showToast('تم إرسال الطلب بنجاح', 'success');
  }
  
  // Send notification to client when request is accepted
  async notifyRequestAccepted(clientId, request) {
    // 1. حفظ الإشعار
    try {
      const notification = {
        user_id: clientId,
        title: 'تم قبول طلبك',
        message: `قام ${request.provider?.fullName || 'مزود الخدمة'} بقبول طلبك لخدمة ${request.service?.name || 'خدمة'}`,
      };
      await supabaseService.client.from('notifications').insert(notification);
    } catch (dbError) {
      console.error('فشل الحفظ:', dbError);
    }

    // 2. إرسال Push Notification
    await this.sendPushNotification(
      clientId, 
      'تم قبول طلبك ✅', 
      `قام ${request.provider?.fullName || 'مزود الخدمة'} بقبول طلبك`, 
      { type: 'request_accepted', requestId: request.id }
    );

    // 3. Toast
    this.showToast('تم قبول الطلب', 'success');
  }

  /**
   * 💬 إرسال إشعار رسالة جديدة للمستخدم الآخر
   * @param {string} recipientId - معرف المستلم
   * @param {string} senderName - اسم المرسل
   * @param {string} messageContent - محتوى الرسالة
   * @param {string} conversationId - معرف المحادثة
   */
  async notifyNewMessage(recipientId, senderName, messageContent, conversationId) {
    console.log('💬 بدء عملية إرسال إشعار رسالة جديدة...');

    // 1. حفظ الإشعار في قاعدة البيانات
    try {
      const notification = {
        user_id: recipientId,
        title: `رسالة جديدة من ${senderName}`,
        message: messageContent.length > 100 
          ? messageContent.substring(0, 100) + '...' 
          : messageContent,
        is_read: false
      };
      
      const saved = await supabaseService.createNotification(notification);
      if (saved) {
        console.log('✅ تم حفظ إشعار الرسالة بنجاح');
      }
    } catch (dbError) {
      console.error('❌ فشل حفظ إشعار الرسالة:', dbError);
    }

    // 2. إرسال Push Notification
    try {
      // اختصار الرسالة للإشعار (أول 100 حرف)
      const shortMessage = messageContent.length > 100 
        ? messageContent.substring(0, 100) + '...' 
        : messageContent;

      const pushSuccess = await this.sendPushNotification(
        recipientId,
        `💬 ${senderName}`,
        shortMessage,
        { 
          type: 'new_message', 
          conversationId: conversationId,
          senderId: supabaseService.currentUser?.id
        }
      );

      if (pushSuccess) {
        console.log('📲 تم إرسال إشعار الرسالة بنجاح');
      } else {
        console.error('❌ فشل إرسال إشعار الرسالة');
      }
    } catch (pushError) {
      console.error('❌ خطأ في إرسال إشعار الرسالة:', pushError);
    }
  }
  
  // دالة مساعدة للعرض
  showToast(message, type = 'success') {
    if (type === 'success') toast.success(message);
    else if (type === 'error') toast.error(message);
    else toast(message, { icon: '🔔' });
  }
}

export default new NotificationService();